import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface SettingsChangelogSectionProps {
  isActive?: boolean;
  refreshKey?: number;
}

/**
 * CHANGELOG.md is served as a static asset from `public/` (copied there by the
 * `prestart`/`prebuild` scripts, single source = the repo-root file) and
 * fetched at runtime. A browser bundle has no filesystem, so it can't be read
 * via `node:fs` — that's what triggered the earlier UnhandledSchemeError.
 */
const CHANGELOG_URL = `${process.env.PUBLIC_URL ?? ""}/CHANGELOG.md`;

const SettingsChangelogSection: React.FC<SettingsChangelogSectionProps> = ({
  isActive = false,
  refreshKey = 0,
}) => {
  const [changelog, setChangelog] = useState("");
  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isActive) return;

    let cancelled = false;
    setIsLoading(true);
    setError(undefined);

    fetch(CHANGELOG_URL)
      .then((res) => {
        if (!res.ok)
          throw new Error(`Failed to load changelog (${res.status})`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setChangelog(text);
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(
            cause instanceof Error
              ? cause.message
              : "Unable to load the changelog.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isActive, refreshKey]);

  if (!isActive) return null;

  return (
    <section
      aria-labelledby="changelog-heading"
      className="flex h-full min-h-0 flex-col gap-3"
    >
      <header className="shrink-0">
        <h2
          id="changelog-heading"
          className="text-lg font-extrabold tracking-tight text-white"
        >
          Release notes
        </h2>
        <p className="mt-1 text-sm text-white/60">
          New features, improvements, and fixes from recent releases.
        </p>
      </header>

      {isLoading && !changelog && (
        <div
          className="flex items-center gap-2 text-sm text-white/70"
          role="status"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading release notes…
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="rounded-xl border border-red-400/30 bg-red-500/15 px-3 py-2 text-sm text-red-100"
        >
          {error}
        </div>
      )}

      {!isLoading && !error && !changelog.trim() && (
        <p className="text-sm text-white/60">No release notes are available.</p>
      )}

      {changelog && (
        <div
          className={[
            "min-h-0 flex-1 overflow-y-auto pr-1 text-sm leading-relaxed text-white/80",
            "[&_h1]:mb-2 [&_h1]:mt-0 [&_h1]:text-lg [&_h1]:font-extrabold [&_h1]:text-white",
            "[&_h2]:mb-1 [&_h2]:mt-5 [&_h2]:text-base [&_h2]:font-extrabold [&_h2]:text-white",
            "[&_h3]:mb-1 [&_h3]:mt-4 [&_h3]:font-bold [&_h3]:text-white",
            "[&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1",
            "[&_a]:font-semibold [&_a]:text-teal-300 [&_a]:underline",
            "[&_code]:rounded [&_code]:bg-white/10 [&_code]:px-1 [&_code]:py-0.5",
          ].join(" ")}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{changelog}</ReactMarkdown>
        </div>
      )}
    </section>
  );
};

export default SettingsChangelogSection;
